/* eslint-disable max-classes-per-file */
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ScopeService } from '@src/app/services/scope/scope.service';
import {
  read,
  utils,
  utils as xlsxUtils,
  WorkBook,
  WorkSheet,
  writeFileXLSX as XLSXWriteFileXLSX,
  WritingOptions,
} from 'xlsx';
import { RetailerXLSXData } from '@src/app/model/RetailerXLSXData';
import { PriceStock } from '@src/app/model/price-stock';
import { SinglePriceStockInput, WoodPriceStockInput } from '@src/app/generated/globalTypes';
import { ItemPriceStock, WoodPriceStock } from '@src/app/model/price-stock-data';
import { WoodType } from '@ess/jg-rule-executor';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { Observable, switchMap } from 'rxjs';
import { first, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ExcelService {
  constructor(
    private _translateService: TranslateService,
    private _scopeService: ScopeService,
    private _apolloService: ApolloService
  ) {
  }

  generateXlsx(
    data: object[],
    fileNameKey: string = 'retailerData.fileNameDefault',
    sheetNameKey: string = 'retailerData.sheetNameDefault'
  ): void {
    const fileName = `${this._translateService.instant(fileNameKey, { retailer: this._scopeService.currentScope$.getValue()?.name ?? 'Retailer' })}.xlsx`;
    const sheetName = this._translateService.instant(sheetNameKey);

    // Create a worksheet from a list of json objects
    const ws: WorkSheet = xlsxUtils.json_to_sheet(data);

    // Create workbook and add the worksheet
    const wb: WorkBook = xlsxUtils.book_new();
    xlsxUtils.book_append_sheet(wb, ws, sheetName);

    // Save to file
    XLSXWriteWrapper.writeFileXLSX(wb, fileName);
  }

  /**
   * Try to read a given file which should contain Retailer EAN data.
   * May throw various errors, which will indicate what exactly went wrong.
   *
   * @param file
   */
  public async readRetailerXlsx$(file: File): Promise<RetailerXLSXData[]> {
    // Set up the promise that handles file reading
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const data = e.target.result;
        const workbook: WorkBook = read(data, {
          type: 'binary',
        });

        if (workbook.SheetNames.length >= 1) {
          const XLSX_json: any[] = xlsxUtils.sheet_to_json<any>(workbook.Sheets[workbook.SheetNames[0]]);
          try {
            const result = this._verifyAndParseSheetData(XLSX_json);
            resolve(result);
          } catch (err) {
            reject(err.message);
          }
        } else {
          reject(this._translateService.instant('retailerData.excelUpload.noSheets'));
        }
      };

      reader.onerror = (ex) => {
        reject(this._translateService.instant('retailerData.excelUpload.readError'));
      };

      // Actually read the file
      reader.readAsBinaryString(file);
    });
  }

  public async readEANXlsx$(file: File): Promise<string[]> {
    return new Promise<string[]>((resolve, reject) => {
      const fileReader = new FileReader();

      fileReader.onload = (event: any) => {
        try {
          const data = event.target.result;
          const workbook = read(data, { type: 'binary' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = utils.sheet_to_json(worksheet, { raw: true });

          const stringArray: string[] = jsonData.map((row: any) => row['ean']);

          resolve(stringArray);
        } catch (error) {
          reject(error);
        }
      };

      fileReader.readAsBinaryString(file);
    });
  }

  /**
   * Given a RetailerXLSXData array of incoming NEW data, a PriceStocks[] of CURRENT stock data, and the scope for which to upload.
   * Will verify the incoming data, parse into separate wood/non-wood PriceStockInput objects, and upload the new lists to the backend.
   * Failure flow is handled through errors, so that we can figure out exactly what went wrong.
   */
  public doPriceStockMutationFromXLSXData$(
    data: RetailerXLSXData[],
    stocks: PriceStock[],
    scope: string
  ): Observable<boolean> {
    const woodPriceStocks: WoodPriceStockInput[] = [];
    const singlePriceStocks: SinglePriceStockInput[] = [];

    // Check if EAN lists match, otherwise throw relevant error
    if (!this._verifyEANListComplete(data, stocks)) {
      throw Error(this._translateService.instant('retailerData.excelUpload.EANMismatch'));
    }

    // Fill stock lists to send to BE
    stocks.forEach((s) => {
      if (s.data instanceof ItemPriceStock) {
        const ipsEan = s.data.EAN;
        const newItem = data.find(d => d.EAN === ipsEan);
        const inputObj: SinglePriceStockInput = new PriceStock(
          undefined,
          s.sku,
          new ItemPriceStock(
            s.data.stock,
            newItem.price,
            ipsEan,
            newItem.internalCode,
            newItem.purchasePrice,
            newItem.discountPrice
          )
        ).toGqlInput() as SinglePriceStockInput;
        singlePriceStocks.push(inputObj);
      } else if (s.data instanceof WoodPriceStock) {
        const prices = new Map<WoodType, ItemPriceStock>();
        Array.from(s.data.prices.entries()).forEach(([wt, ips]) => {
          const newItem = data.find(d => d.EAN === ips.EAN);
          prices.set(
            wt,
            new ItemPriceStock(
              ips.stock,
              newItem.price,
              ips.EAN, newItem.internalCode,
              newItem.purchasePrice,
              newItem.discountPrice
            )
          );
        });
        const inputObj: WoodPriceStockInput = new PriceStock(
          undefined,
          s.sku,
          new WoodPriceStock(prices)
        ).toGqlInput() as WoodPriceStockInput;
        woodPriceStocks.push(inputObj);
      }
    });

    // First upload single; if success, then upload wood
    // Throw specific error to differentiate where things go wrong (if they do)
    return this._apolloService.uploadSinglePriceStocks$(
      { priceStocks: singlePriceStocks } as object as JSON,
      [scope]
    ).pipe(first(), switchMap((result) => {
      if (!result) {
        throw Error(this._translateService.instant('retailerData.excelUpload.singleUploadFail'));
      } else {
        return this._apolloService.uploadWoodPriceStocks$(
          { priceStocks: woodPriceStocks } as object as JSON, [scope]
        );
      }
    })).pipe(first(), map((result) => {
      if (!result) {
        throw Error(this._translateService.instant('retailerData.excelUpload.woodUploadFail'));
      } else {
        return result;
      }
    }));
  }

  /**
   * Given an input set of data and existing set of stocks, check if each and every EAN in the new list is also present
   * in the old list, and vice-versa.
   */
  private _verifyEANListComplete(data: RetailerXLSXData[], stocks: PriceStock[]): boolean {
    const dataEanList: string[] = data.map(d => d.EAN);
    const stockEanList: string[] = [];
    stocks.forEach((s) => {
      if (s.data instanceof ItemPriceStock) {
        stockEanList.push(s.data.EAN);
      } else if (s.data instanceof WoodPriceStock) {
        Array.from(s.data.prices.values()).forEach(
          woodStock => stockEanList.push(woodStock.EAN)
        );
      }
    });
    return dataEanList.every(ean => stockEanList.includes(ean)) && stockEanList.every(ean => dataEanList.includes(ean));
  }

  /**
   * Given a json-ified XLSX sheet, will verify that the data matches the format we expect (RetailerXLSXData)
   * and will parse to RetailerXLSXData[]
   */
  private _verifyAndParseSheetData(sheetAsJson: any[]): RetailerXLSXData[] {
    if (sheetAsJson.length > 0) {
      // every item should have a non-falsy value for every field.
      if (!sheetAsJson.every(row => row.hasOwnProperty('EAN') && !!row.EAN)) {
        throw Error(this._translateService.instant('retailerData.excelUpload.badEanRow'));
      }
      // every item should have a price entry (may be falsy)
      if (!sheetAsJson.every(row => row.hasOwnProperty('price') && !!row.price)) {
        throw Error(this._translateService.instant('retailerData.excelUpload.badPriceRow'));
      }
      // every item should have an internal code
      if (!sheetAsJson.every(row => row.hasOwnProperty('internalCode') && !!row.internalCode)) {
        throw Error(this._translateService.instant('retailerData.excelUpload.badInternalCodeRow'));
      }
      return sheetAsJson.map(
        row => new RetailerXLSXData(row.EAN, row.price, row.internalCode, row.purchasePrice, row.discountPrice)
      );
    } else {
      throw Error(this._translateService.instant('retailerData.excelUpload.emptySheet'));
    }
  }
}

/**
 * Wrapper for testability
 */
export class XLSXWriteWrapper {
  public static writeFileXLSX(data: WorkBook, filename: string, opts?: WritingOptions) {
    if (opts) {
      return XLSXWriteFileXLSX(data, filename, opts);
    } else {
      return XLSXWriteFileXLSX(data, filename);
    }
  }
}

/* eslint-enable max-classes-per-file */
